import React from "react";
import compose from "ramda/src/compose";
import Switch from '@mui/material/Switch';

import Api from "../../services/Api";
import useAuth from "../../routes/login/AuthContext";

import { Grid } from "../../UI/Grid";
import { Button } from "../../UI/Button";
import { Typography } from "../../UI/Typography";
import { COLOR_WHITE } from "../../constants/styles";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { useSnackbar } from "../../hooks/useSnackbar";
import { IExtendedL3 } from "../../services/NetworkApi";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";

interface IProps {
  unitTypes: IExtendedL3[];
  updateData: React.Dispatch<React.SetStateAction<IExtendedL3[]>>;
  getData: () => Promise<void>;
  showArchived: boolean;
  changeArchivedVisiblity: (_: any, checked: boolean) => void;
}

export const SetAllToButtons: React.FC<IProps> = (props) => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { loadNetworkInfoForNetworks } = useNetworkContext();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [isAllowLoading, setIsAllowLoading] = React.useState(false);
  const [isNotAllowLoading, setIsNotAllowLoading] = React.useState(false);

  const handleError = () =>
    showErrorSnackbar("An error occurred. Please try again");

  const onComplete = async () => {
    await loadNetworkInfoForNetworks([selectedNetworkId]);
    showSuccessSnackbar("Saved Changes");
    await props.getData();
  };
  const statusFilter = (item: IExtendedL3) =>  item.matchingRoundStatus !== "ARCHIVED" && item.matchingRoundStatus !== "CLOSED";

  const updateLocalData = (newNewMatchingAllowed: boolean) => {
    const archivedandClosed = props.unitTypes.filter((item: IExtendedL3) => !statusFilter(item));
    const newData = props.unitTypes.filter(statusFilter).map(unitType => ({
      ...unitType, 
      newNewMatchingAllowed: newNewMatchingAllowed,
      newNewFindingAllowed: newNewMatchingAllowed
    }));
    props.updateData([...newData,...archivedandClosed]);
  };

  const allowAll = async () => {
    const body = {
      subnetworkCommands: [],
      unitTypeCommands: props.unitTypes
      .filter(statusFilter)
      .map((item) => ({
        l1Id: selectedNetworkId,
        l2Id: item.l2Id,
        l3Id: item.l3Id,
        allowNewNewMatching: true,
        allowNewNewFinding: true,
      })),
    };

    setIsAllowLoading(true);

    Api.setMathingRules(selectedNetworkId, body, await getAuthToken())
      .then(compose(() => updateLocalData(true)), onComplete)
      .catch(handleError)
      .finally(() => setIsAllowLoading(false));
  };

  const notAllowAll = async () => {
    const body = {
      subnetworkCommands: [],
      unitTypeCommands: props.unitTypes
      .filter(statusFilter)
      .map((item) => ({
        l1Id: selectedNetworkId,
        l2Id: item.l2Id,
        l3Id: item.l3Id,
        allowNewNewMatching: false,
        allowNewNewFinding: false,
      })),
    };

    setIsNotAllowLoading(true);

    Api.setMathingRules(selectedNetworkId, body, await getAuthToken())
      .then(compose(() => updateLocalData(false)), onComplete)
      .catch(handleError)
      .finally(() => setIsNotAllowLoading(false));
  };

  return (
    <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
      <Grid item>
        <Typography color={COLOR_WHITE}> Show Archived
          <Switch value={props.showArchived} onChange={props.changeArchivedVisiblity}/>
        </Typography>
      </Grid> 
      <Grid item>
        <Typography color={COLOR_WHITE}>Set All To:</Typography>
      </Grid>
      <Grid item>
        <ReadOnlyWrapper>
          <Button
            isLoading={isAllowLoading}
            isDisabled={isNotAllowLoading || isSuperAdminRead()}
            appearance="primary"
            onClick={allowAll}
          >
            Allowed
          </Button>
        </ReadOnlyWrapper>
      </Grid>
      <Grid item>
        <ReadOnlyWrapper>
          <Button
            isDisabled={isAllowLoading || isSuperAdminRead()}
            isLoading={isNotAllowLoading}
            appearance="primary"
            onClick={notAllowAll}
          >
            Not Allowed
          </Button>
        </ReadOnlyWrapper>
      </Grid>
    </Grid>
  );
};
